import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
    getAuth,
    signInWithEmailAndPassword,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    signOut,
    onAuthStateChanged
} from 'firebase/auth';
import { googleProvider } from '../firebase/config';
import {
    getUserData,
    createOrUpdateUser,
    updateUserDisplayName
} from '../services/tracker-limited-data';

// Tworzenie kontekstu uwierzytelniania
const AuthContext = createContext();

// Hook do użycia kontekstu autentykacji
export const useAuth = () => {
    return useContext(AuthContext);
};

// Provider kontekstu uwierzytelniania
export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const auth = getAuth();

    const syncUserProfile = useCallback(async (user) => {
        const existingUser = await getUserData(user.uid);

        if (existingUser) {
            await createOrUpdateUser(user.uid, {
                email: user.email,
                display_name: existingUser.display_name || user.displayName || user.email?.split('@')[0] || 'Użytkownik',
                photo_url: user.photoURL
            });
        } else {
            await createOrUpdateUser(user.uid, {
                email: user.email,
                display_name: user.displayName || user.email?.split('@')[0] || 'Użytkownik',
                photo_url: user.photoURL,
                role: 'user'
            });
        }

        return getUserData(user.uid);
    }, []);

    const refreshUserData = useCallback(async () => {
        const user = auth.currentUser;
        if (!user) return null;
        const refreshedUserData = await getUserData(user.uid);
        setUserData(refreshedUserData);
        return refreshedUserData;
    }, [auth]);

    const isVercelPreview = () => window.location.hostname.endsWith('.vercel.app');

    const shouldUseRedirectFallback = (err) => {
        const popupErrorCodes = [
            'auth/popup-blocked',
            'auth/cancelled-popup-request'
        ];

        return popupErrorCodes.includes(err?.code) || (isVercelPreview() && err?.code === 'auth/popup-closed-by-user');
    };

    // Logowanie administratora (email/hasło)
    const login = async (email, password) => {
        try {
            setError(null);
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            return userCredential.user;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // Logowanie przez Google
    const loginWithGoogle = async () => {
        try {
            setError(null);
            if (isVercelPreview()) {
                await signInWithRedirect(auth, googleProvider);
                return null;
            }

            const result = await signInWithPopup(auth, googleProvider);

            if (result.user) {
                const syncedUserData = await syncUserProfile(result.user);
                setUserData(syncedUserData);
            }

            return result.user;
        } catch (err) {
            if (shouldUseRedirectFallback(err)) {
                await signInWithRedirect(auth, googleProvider);
                return null;
            }

            setError(err.message);
            throw err;
        }
    };

    // Wylogowanie użytkownika
    const logout = async () => {
        try {
            await signOut(auth);
            setUserData(null); // Wyczyść dane użytkownika
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // Aktualizuj display_name użytkownika
    const updateDisplayName = async (newDisplayName) => {
        try {
            if (!currentUser) {
                throw new Error('Użytkownik nie jest zalogowany');
            }

            await updateUserDisplayName(currentUser.uid, newDisplayName);

            // Odśwież dane użytkownika
            const updatedUserData = await getUserData(currentUser.uid);
            setUserData(updatedUserData);

            return true;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // Sprawdzanie stanu uwierzytelnienia
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setCurrentUser(user);
                try {
                    const syncedUserData = await syncUserProfile(user);
                    setUserData(syncedUserData);
                } catch (error) {
                    console.error('Błąd podczas pobierania danych użytkownika:', error);
                    setUserData(null);
                }
            } else {
                setCurrentUser(null);
                setUserData(null);
            }

            setLoading(false);
        });

        return unsubscribe;
    }, [auth, syncUserProfile]);

    useEffect(() => {
        getRedirectResult(auth)
            .then(async (result) => {
                if (result?.user) {
                    const syncedUserData = await syncUserProfile(result.user);
                    setCurrentUser(result.user);
                    setUserData(syncedUserData);
                }
            })
            .catch((err) => {
                console.error('Błąd podczas finalizowania logowania Google:', err);
                setError(err.message);
            });
    }, [auth, syncUserProfile]);

    const value = {
        currentUser,
        userData,
        login,
        loginWithGoogle,
        logout,
        updateDisplayName,
        refreshUserData,
        loading,
        error
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
